/**
 * RoomsEditor — Sell Hub "Rooms & dimensions" section.
 *
 * Owns the rooms[] array. Renders one `RoomCard` per row, an "Add room"
 * button, a running totals chip, an autosave chip, and (optionally) a
 * legacy-without-rooms migration banner.
 *
 * State semantics:
 *  - Drafts are tolerant — partial rows (missing L/W) save fine.
 *  - Publish gates on `getPublishBlockingErrors()` exposed via `useImperativeHandle`
 *    so the parent wizard can hook the Review step's publish gate without
 *    duplicating validation. (Caller is responsible for calling it.)
 *
 * Autosave:
 *  - `onAutosave` is invoked on a 10 s debounce while dirty, and immediately
 *    on input blur. The caller decides what to PATCH — by design we just
 *    forward the rooms array; the parent rolls it into the full draft save.
 *
 * Props:
 *  - rooms: Room[] — full array, parent-owned (RoomCard does not own its
 *      position; the editor does the splice)
 *  - onChange: (rooms: Room[]) => void
 *  - onAutosave: (rooms: Room[]) => Promise<void> | void — optional. When
 *      provided, the editor manages the autosave chip lifecycle.
 *  - hadLegacyTotalRooms: boolean — if the listing has a legacy
 *      mls_profile.total_rooms but no structured rows, show the
 *      "Old total: N rooms" banner.
 *  - legacyTotalRoomsCount: number
 *  - showErrors: boolean — pass true on publish attempt for hard validation.
 *  - disclaimer: string — legal help-text rendered under the section hint.
 */

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import RoomCard from './RoomCard';
import { ROOMS_MAX } from './roomConstants';
import './RoomsEditor.css';

const AUTOSAVE_DEBOUNCE_MS = 10000;

const emptyRoom = () => ({
  room_type: '',
  level: '',
  length_ft: '',
  width_ft: '',
  flooring: '',
  features: '',
});

const parseDim = (raw) => {
  if (raw === '' || raw == null) return null;
  const n = parseFloat(raw);
  return Number.isNaN(n) ? null : n;
};

const dimValid = (n) => n != null && n > 0 && n <= 99.9;

const roomTotals = (rooms) => {
  let count = rooms.length;
  let sqft = 0;
  rooms.forEach((r) => {
    const l = parseDim(r.length_ft);
    const w = parseDim(r.width_ft);
    if (dimValid(l) && dimValid(w)) sqft += l * w;
  });
  return { count, sqft: Math.round(sqft) };
};

/**
 * Returns an array of human-readable strings explaining why publish is
 * blocked. Empty array means publish is allowed.
 */
export function getRoomsPublishErrors(rooms) {
  const errs = [];
  rooms.forEach((r, idx) => {
    const l = parseDim(r.length_ft);
    const w = parseDim(r.width_ft);
    const num = idx + 1;
    if (!r.room_type) errs.push(`Room ${num}: pick a room type.`);
    if (!r.level) errs.push(`Room ${num}: pick a level.`);
    if (l == null || w == null) errs.push(`Room ${num}: add L and W.`);
    else {
      if (!dimValid(l)) errs.push(`Room ${num}: length must be between 0.1 and 99.9 ft.`);
      if (!dimValid(w)) errs.push(`Room ${num}: width must be between 0.1 and 99.9 ft.`);
    }
  });
  return errs;
}

const formatRelative = (ts) => {
  if (!ts) return '';
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
};

const RoomsEditor = forwardRef(function RoomsEditor(
  {
    rooms,
    onChange,
    onAutosave,
    hadLegacyTotalRooms = false,
    legacyTotalRoomsCount = 0,
    showErrors = false,
    disclaimer,
  },
  ref,
) {
  const { count, sqft } = useMemo(() => roomTotals(rooms), [rooms]);
  const atCap = rooms.length >= ROOMS_MAX;

  // Autosave state machine: 'idle' → 'dirty' → 'saving' → 'saved' or 'failed'.
  // Chip rendering reads off saveState + lastSavedAt + lastError.
  const [saveState, setSaveState] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  const debounceRef = useRef(null);
  const dirtyRef = useRef(false);
  const lastSavedRoomsRef = useRef(rooms);
  // Tick every second so the "Autosaved 4s ago" chip stays current.
  const [, setNowTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setNowTick((n) => (n + 1) % 1000), 1000);
    return () => clearInterval(interval);
  }, []);

  // Banner dismissal — once dismissed, the legacy banner stays gone for the
  // life of the session. (Persistence across reloads is server-side and out
  // of scope here.)
  const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);

  const showLegacyBanner =
    hadLegacyTotalRooms && rooms.length === 0 && !legacyBannerDismissed;

  // Schedule an autosave a fixed time after the last edit (debounced).
  const triggerAutosave = useCallback(
    (nextRooms) => {
      if (!onAutosave) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      dirtyRef.current = true;
      setSaveState('dirty');
      debounceRef.current = setTimeout(async () => {
        try {
          setSaveState('saving');
          await onAutosave(nextRooms);
          setSaveState('saved');
          setLastSavedAt(Date.now());
          setLastError(null);
          lastSavedRoomsRef.current = nextRooms;
          dirtyRef.current = false;
        } catch (err) {
          console.error('Autosave failed:', err);
          setSaveState('failed');
          setLastError(err?.message || 'Autosave failed.');
        }
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [onAutosave],
  );

  // Cleanup pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Expose validation + a force-save handle to the parent.
  useImperativeHandle(
    ref,
    () => ({
      getPublishBlockingErrors: () => getRoomsPublishErrors(rooms),
      getRooms: () => rooms,
      getTotals: () => ({ count, sqft }),
    }),
    [rooms, count, sqft],
  );

  const replaceAt = (idx, patch) => {
    const next = rooms.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
    triggerAutosave(next);
  };

  const removeAt = (idx) => {
    const next = rooms.filter((_, i) => i !== idx);
    onChange(next);
    triggerAutosave(next);
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= rooms.length) return;
    const next = [...rooms];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
    triggerAutosave(next);
  };

  const addRoomButtonRef = useRef(null);
  const lastCardRef = useRef(null);

  const addRoom = () => {
    if (atCap) return;
    const next = [...rooms, emptyRoom()];
    onChange(next);
    triggerAutosave(next);
    // Scroll the new card into view + focus its first input next tick.
    requestAnimationFrame(() => {
      if (lastCardRef.current) {
        lastCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const sel = lastCardRef.current.querySelector('select');
        if (sel) sel.focus();
      }
    });
  };

  // Autosave chip — three string states.
  const chip = useMemo(() => {
    if (saveState === 'saving') {
      return { tone: 'info', text: 'Saving…', pulse: true };
    }
    if (saveState === 'failed') {
      return {
        tone: 'warning',
        text: "Couldn't autosave — we'll keep trying",
        pulse: false,
      };
    }
    if (saveState === 'saved' && lastSavedAt) {
      return {
        tone: 'success',
        text: `Autosaved ${formatRelative(lastSavedAt)}`,
        pulse: false,
      };
    }
    if (saveState === 'dirty') {
      return { tone: 'neutral', text: 'Unsaved changes', pulse: false };
    }
    return null;
  }, [saveState, lastSavedAt]);

  return (
    <section
      className="rooms-editor"
      aria-labelledby="rooms-editor-title"
      data-testid="rooms-editor"
    >
      <header className="rooms-editor__head">
        <h3
          id="rooms-editor-title"
          className="listing-form__section-title rooms-editor__title"
        >
          Rooms &amp; dimensions
        </h3>
        <p className="listing-form__hint">
          Add each room with its size. You can add up to {ROOMS_MAX} rooms.
        </p>
        {disclaimer && (
          <p className="rooms-editor__disclaimer" data-testid="rooms-editor-disclaimer">
            {disclaimer}
          </p>
        )}

        <div className="rooms-editor__chips">
          <span className="rooms-editor__totals" data-testid="rooms-editor-totals">
            {count} {count === 1 ? 'room' : 'rooms'} ·{' '}
            {sqft.toLocaleString()} sf
          </span>
          {chip && (
            <span
              className={`rooms-editor__autosave rooms-editor__autosave--${chip.tone}`}
              data-testid="rooms-editor-autosave"
              role="status"
              aria-live="polite"
            >
              <span
                className={`rooms-editor__autosave-dot ${chip.pulse ? 'rooms-editor__autosave-dot--pulse' : ''}`}
                aria-hidden="true"
              />
              {chip.text}
            </span>
          )}
        </div>
      </header>

      {showLegacyBanner && (
        <div
          className="rooms-editor__legacy-banner"
          role="status"
          data-testid="rooms-editor-legacy-banner"
        >
          <div>
            <strong>Old total: {legacyTotalRoomsCount} rooms.</strong>
            <p>
              Add them one-by-one to enable buyer filtering. We&apos;ll keep your
              existing summary until you publish.
            </p>
          </div>
          <button
            type="button"
            className="rooms-editor__legacy-banner-close"
            aria-label="Dismiss legacy rooms banner"
            onClick={() => setLegacyBannerDismissed(true)}
          >
            ×
          </button>
        </div>
      )}

      {rooms.length === 0 ? (
        <div className="rooms-editor__empty" data-testid="rooms-editor-empty">
          <span className="rooms-editor__empty-icon" aria-hidden="true">🏠</span>
          <h4 className="rooms-editor__empty-title">No rooms added yet</h4>
          <p className="rooms-editor__empty-hint">
            Add your kitchen, bedrooms, baths, and any other rooms you want
            buyers to see.
          </p>
          <button
            type="button"
            className="rooms-editor__empty-btn"
            onClick={addRoom}
            data-testid="rooms-editor-add-first"
          >
            <span aria-hidden="true">＋</span> Add your first room
          </button>
        </div>
      ) : (
        <ul
          className="rooms-editor__list"
          aria-label="Rooms"
          data-testid="rooms-editor-list"
        >
          {rooms.map((room, idx) => (
            <div
              key={room.id != null ? `id-${room.id}` : `pos-${idx}`}
              ref={idx === rooms.length - 1 ? lastCardRef : undefined}
            >
              <RoomCard
                room={room}
                index={idx}
                total={rooms.length}
                showErrors={showErrors}
                onChange={(patch) => replaceAt(idx, patch)}
                onDelete={() => removeAt(idx)}
                onMoveUp={() => move(idx, -1)}
                onMoveDown={() => move(idx, 1)}
              />
            </div>
          ))}
        </ul>
      )}

      {rooms.length > 0 && (
        <div className="rooms-editor__add-wrap">
          <button
            type="button"
            ref={addRoomButtonRef}
            className="rooms-editor__add-btn"
            onClick={addRoom}
            disabled={atCap}
            data-testid="rooms-editor-add"
          >
            <span aria-hidden="true">＋</span> Add room
          </button>
          <p className="rooms-editor__add-count">
            {atCap
              ? `Maximum ${ROOMS_MAX} rooms reached.`
              : `${rooms.length} of ${ROOMS_MAX} rooms used`}
          </p>
        </div>
      )}

      {lastError && saveState === 'failed' && (
        <p
          className="rooms-editor__autosave-toast"
          role="alert"
          data-testid="rooms-editor-autosave-error"
        >
          {lastError}
        </p>
      )}
    </section>
  );
});

export default RoomsEditor;
